// src/modules/ida/components/ChatInput.test.tsx
//
// Unit tests for ChatInput component.
// Note: Uses fireEvent instead of userEvent for JoyUI Textarea compatibility.

import React from "react";
import { render, screen, fireEvent } from "@testing-library/react";
import "@testing-library/jest-dom";
import { ChatInput } from "./ChatInput";

describe("ChatInput", () => {
  const defaultProps = {
    onSend: jest.fn(),
    onClear: jest.fn(),
    disabled: false,
  };

  beforeEach(() => {
    jest.clearAllMocks();
  });

  // ---------------------------------------------------------------------------
  // Rendering Tests
  // ---------------------------------------------------------------------------

  describe("Rendering", () => {
    it("renders textarea with default placeholder", () => {
      render(<ChatInput {...defaultProps} />);

      expect(
        screen.getByPlaceholderText("Ask me anything about your documents..."),
      ).toBeInTheDocument();
    });

    it("renders textarea with custom placeholder", () => {
      render(<ChatInput {...defaultProps} placeholder="Custom placeholder" />);

      expect(
        screen.getByPlaceholderText("Custom placeholder"),
      ).toBeInTheDocument();
    });

    it("renders send button", () => {
      render(<ChatInput {...defaultProps} />);

      expect(screen.getByRole("button", { name: /send/i })).toBeInTheDocument();
    });

    it("renders clear button when hasMessages is true", () => {
      render(<ChatInput {...defaultProps} hasMessages={true} />);

      expect(
        screen.getByRole("button", { name: /clear/i }),
      ).toBeInTheDocument();
    });

    it("does not render clear button when hasMessages is false", () => {
      render(<ChatInput {...defaultProps} hasMessages={false} />);

      expect(
        screen.queryByRole("button", { name: /clear/i }),
      ).not.toBeInTheDocument();
    });
  });

  // ---------------------------------------------------------------------------
  // Input Behavior Tests
  // ---------------------------------------------------------------------------

  describe("Input Behavior", () => {
    it("updates input value when typing", () => {
      render(<ChatInput {...defaultProps} />);

      const textarea = screen.getByRole("textbox");
      fireEvent.change(textarea, { target: { value: "Hello world" } });

      expect(textarea).toHaveValue("Hello world");
    });

    it("limits input to 2000 characters", () => {
      render(<ChatInput {...defaultProps} />);

      const textarea = screen.getByRole("textbox");
      const longText = "a".repeat(2100);
      fireEvent.change(textarea, { target: { value: longText } });

      expect(
        (textarea as HTMLTextAreaElement).value.length,
      ).toBeLessThanOrEqual(2000);
    });

    it("disables textarea when disabled prop is true", () => {
      render(<ChatInput {...defaultProps} disabled={true} />);

      const textarea = screen.getByRole("textbox");
      expect(textarea).toBeDisabled();
    });
  });

  // ---------------------------------------------------------------------------
  // Send Button Tests
  // ---------------------------------------------------------------------------

  describe("Send Button", () => {
    it("send button is disabled when input is empty", () => {
      render(<ChatInput {...defaultProps} />);

      const sendButton = screen.getByRole("button", { name: /send/i });
      expect(sendButton).toBeDisabled();
    });

    it("send button is disabled when input is only whitespace", () => {
      render(<ChatInput {...defaultProps} />);

      const textarea = screen.getByRole("textbox");
      fireEvent.change(textarea, { target: { value: "   " } });

      const sendButton = screen.getByRole("button", { name: /send/i });
      expect(sendButton).toBeDisabled();
    });

    it("send button is enabled when input has text", () => {
      render(<ChatInput {...defaultProps} />);

      const textarea = screen.getByRole("textbox");
      fireEvent.change(textarea, { target: { value: "Hello" } });

      const sendButton = screen.getByRole("button", { name: /send/i });
      expect(sendButton).not.toBeDisabled();
    });

    it("send button is disabled when component is disabled", () => {
      const { rerender } = render(<ChatInput {...defaultProps} />);

      const textarea = screen.getByRole("textbox");
      fireEvent.change(textarea, { target: { value: "Hello" } });

      rerender(<ChatInput {...defaultProps} disabled={true} />);

      const sendButton = screen.getByRole("button", { name: /send/i });
      expect(sendButton).toBeDisabled();
    });

    it("calls onSend with trimmed message when send button clicked", () => {
      const onSend = jest.fn();
      render(<ChatInput {...defaultProps} onSend={onSend} />);

      const textarea = screen.getByRole("textbox");
      fireEvent.change(textarea, { target: { value: "  Hello world  " } });

      const sendButton = screen.getByRole("button", { name: /send/i });
      fireEvent.click(sendButton);

      expect(onSend).toHaveBeenCalledWith("Hello world");
    });

    it("clears input after sending message", () => {
      render(<ChatInput {...defaultProps} />);

      const textarea = screen.getByRole("textbox");
      fireEvent.change(textarea, { target: { value: "Test message" } });

      const sendButton = screen.getByRole("button", { name: /send/i });
      fireEvent.click(sendButton);

      expect(textarea).toHaveValue("");
    });
  });

  // ---------------------------------------------------------------------------
  // Keyboard Handling Tests
  // ---------------------------------------------------------------------------

  describe("Keyboard Handling", () => {
    it("sends message on Enter key press", () => {
      const onSend = jest.fn();
      render(<ChatInput {...defaultProps} onSend={onSend} />);

      const textarea = screen.getByRole("textbox");
      fireEvent.change(textarea, { target: { value: "Test message" } });
      fireEvent.keyDown(textarea, {
        key: "Enter",
        code: "Enter",
        shiftKey: false,
      });

      expect(onSend).toHaveBeenCalledWith("Test message");
    });

    it("does not send message on Shift+Enter (allows newline)", () => {
      const onSend = jest.fn();
      render(<ChatInput {...defaultProps} onSend={onSend} />);

      const textarea = screen.getByRole("textbox");
      fireEvent.change(textarea, { target: { value: "Line 1" } });
      fireEvent.keyDown(textarea, {
        key: "Enter",
        code: "Enter",
        shiftKey: true,
      });

      expect(onSend).not.toHaveBeenCalled();
    });

    it("does not send when Enter pressed with empty input", () => {
      const onSend = jest.fn();
      render(<ChatInput {...defaultProps} onSend={onSend} />);

      const textarea = screen.getByRole("textbox");
      fireEvent.keyDown(textarea, {
        key: "Enter",
        code: "Enter",
        shiftKey: false,
      });

      expect(onSend).not.toHaveBeenCalled();
    });

    it("does not send when Enter pressed while disabled", () => {
      const onSend = jest.fn();
      render(<ChatInput {...defaultProps} onSend={onSend} disabled={true} />);

      const textarea = screen.getByRole("textbox");
      // Note: disabled textarea won't respond to keyDown, but we test the logic anyway
      fireEvent.keyDown(textarea, {
        key: "Enter",
        code: "Enter",
        shiftKey: false,
      });

      expect(onSend).not.toHaveBeenCalled();
    });
  });

  // ---------------------------------------------------------------------------
  // Clear Button Tests
  // ---------------------------------------------------------------------------

  describe("Clear Button", () => {
    it("clear button is disabled when component is disabled", () => {
      render(
        <ChatInput {...defaultProps} hasMessages={true} disabled={true} />,
      );

      const clearButton = screen.getByRole("button", { name: /clear/i });
      expect(clearButton).toBeDisabled();
    });

    it("calls onClear when clear button clicked", () => {
      const onClear = jest.fn();
      render(
        <ChatInput {...defaultProps} onClear={onClear} hasMessages={true} />,
      );

      const clearButton = screen.getByRole("button", { name: /clear/i });
      fireEvent.click(clearButton);

      expect(onClear).toHaveBeenCalled();
    });

    it("clear button is not rendered when no messages", () => {
      render(<ChatInput {...defaultProps} hasMessages={false} />);

      // Clear button shouldn't be rendered at all when no messages
      expect(
        screen.queryByRole("button", { name: /clear/i }),
      ).not.toBeInTheDocument();
    });
  });

  // ---------------------------------------------------------------------------
  // Disabled State Tests
  // ---------------------------------------------------------------------------

  describe("Disabled State", () => {
    it("shows custom placeholder when disabled", () => {
      render(
        <ChatInput
          {...defaultProps}
          disabled={true}
          placeholder="Processing..."
        />,
      );

      expect(screen.getByPlaceholderText("Processing...")).toBeInTheDocument();
    });

    it("all interactive elements are disabled when disabled", () => {
      render(
        <ChatInput {...defaultProps} disabled={true} hasMessages={true} />,
      );

      const textarea = screen.getByRole("textbox");
      const sendButton = screen.getByRole("button", { name: /send/i });
      const clearButton = screen.getByRole("button", { name: /clear/i });

      expect(textarea).toBeDisabled();
      expect(sendButton).toBeDisabled();
      expect(clearButton).toBeDisabled();
    });
  });
});
